export type SavedContentTypeId =
  | 'post'
  | 'video'
  | 'evento'
  | 'concurso'
  | 'programa'
  | 'locutor'
  | 'radio';

export type SavedFilterId = 'recientes' | 'mas-vistos' | 'favoritos' | 'pendientes';

export interface SavedContentType {
  id: SavedContentTypeId;
  name: string;
  icon: string;
  description: string;
  color: string;
  bgColor: string;
  textColor: string;
  totalSaved: number;
}

export interface SavedItem {
  id: number;
  type: SavedContentTypeId;
  title: string;
  subtitle: string;
  thumbnailUrl: string;
  authorName: string;
  authorPhoto: string;
  radioName: string;
  radioFreq: string;
  tags: string[];
  savedAt: string;
  viewCount: number;
  isFavorite: boolean;
  isPending: boolean;
  scoreValue: number;
  excerpt: string;
}

export interface SavedUserProfile {
  id: number;
  name: string;
  photo: string;
  totalSaved: number;
  totalViews: number;
  totalFavorites: number;
  totalPending: number;
  streak: number;
}

export interface SavedDashboardStats {
  totalSaved: number;
  totalSavedToday: number;
  mostSavedType: string;
  mostSavedTypeCount: number;
  totalViews: number;
  totalFavorites: number;
  totalPending: number;
  totalScoreGenerated: number;
  avgSavedPerUser: number;
}

export interface SaveScoreAction {
  id: string;
  label: string;
  description: string;
  points: number;
  icon: string;
  color: string;
}

export const savedContentTypes: SavedContentType[] = [
  {
    id: 'post',
    name: 'Posts',
    icon: 'ri-article-line',
    description: 'Publicaciones, noticias y actualizaciones que querés leer después.',
    color: 'text-sky-600',
    bgColor: 'bg-sky-50',
    textColor: 'text-sky-700',
    totalSaved: 18400,
  },
  {
    id: 'video',
    name: 'Videos',
    icon: 'ri-video-line',
    description: 'Contenido audiovisual que merece ser visto con tiempo.',
    color: 'text-rose-600',
    bgColor: 'bg-rose-50',
    textColor: 'text-rose-700',
    totalSaved: 23100,
  },
  {
    id: 'evento',
    name: 'Eventos',
    icon: 'ri-calendar-event-line',
    description: 'Eventos que no te querés perder y guardás para estar listo.',
    color: 'text-violet-600',
    bgColor: 'bg-violet-50',
    textColor: 'text-violet-700',
    totalSaved: 12600,
  },
  {
    id: 'concurso',
    name: 'Concursos',
    icon: 'ri-trophy-line',
    description: 'Competencias para participar o seguir de cerca los resultados.',
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    textColor: 'text-amber-700',
    totalSaved: 9800,
  },
  {
    id: 'programa',
    name: 'Programas',
    icon: 'ri-broadcast-line',
    description: 'Programas de radio que no te querés perder ningún episodio.',
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-50',
    textColor: 'text-indigo-700',
    totalSaved: 21500,
  },
  {
    id: 'locutor',
    name: 'Locutores',
    icon: 'ri-user-voice-line',
    description: 'Voces que te inspiran y querés seguir de cerca.',
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
    textColor: 'text-emerald-700',
    totalSaved: 8700,
  },
  {
    id: 'radio',
    name: 'Radios',
    icon: 'ri-radio-line',
    description: 'Emisoras que sintonizás siempre y querés tener a mano.',
    color: 'text-teal-600',
    bgColor: 'bg-teal-50',
    textColor: 'text-teal-700',
    totalSaved: 31200,
  },
];

export const savedItems: SavedItem[] = [
  {
    id: 1,
    type: 'post',
    title: 'Guía definitiva: Cómo armar tu primer programa de radio en vivo desde cero',
    subtitle: 'Equipo, software y mentalidad',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Modern%20minimalist%20home%20radio%20studio%20setup%20with%20microphone%20headphones%20and%20laptop%20on%20wooden%20desk%20warm%20ambient%20lighting%20creative%20workspace%20professional%20broadcast%20environment%20clean%20aesthetic&width=800&height=450&seq=svd-thumb-post-guia&orientation=landscape',
    authorName: 'Radio Élite Digital',
    authorPhoto: 'https://readdy.ai/api/search-image?query=Radio%20station%20logo%20modern%20circular%20minimalist%20design%20warm%20golden%20accents%20on%20dark%20background%20professional%20broadcasting%20emblem%20sleek%20contemporary&width=120&height=120&seq=svd-author-elite&orientation=squarish',
    radioName: 'Radio Élite Digital',
    radioFreq: '99.1 FM',
    tags: ['tutorial', 'principiantes', 'equipo'],
    savedAt: 'Hace 2 horas',
    viewCount: 45200,
    isFavorite: true,
    isPending: false,
    scoreValue: 5,
    excerpt: 'Todo lo que necesitás saber para empezar a transmitir con calidad profesional. Desde el micrófono hasta el software de streaming.',
  },
  {
    id: 2,
    type: 'video',
    title: 'El backstage completo de la final de Copas RFW — Lo que no se vio al aire',
    subtitle: 'Detrás de cámaras exclusivo',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Behind%20the%20scenes%20at%20a%20radio%20competition%20final%20event%20backstage%20moments%20with%20hosts%20preparing%20warm%20candid%20interactions%20production%20crew%20at%20work%20professional%20event%20photography%20warm%20ambient%20lighting&width=800&height=450&seq=svd-thumb-video-copas&orientation=landscape',
    authorName: 'RFW Oficial',
    authorPhoto: 'https://readdy.ai/api/search-image?query=Official%20RFW%20platform%20logo%20professional%20modern%20circular%20design%20white%20and%20gold%20accents%20confident%20branding%20emblem&width=120&height=120&seq=svd-author-rfw&orientation=squarish',
    radioName: 'RFW Oficial',
    radioFreq: 'Global',
    tags: ['exclusivo', 'backstage', 'copas'],
    savedAt: 'Hace 5 horas',
    viewCount: 89300,
    isFavorite: true,
    isPending: false,
    scoreValue: 8,
    excerpt: '30 minutos de material nunca antes visto. Las emociones, los nervios y la celebración desde adentro del evento más importante de la radio.',
  },
  {
    id: 3,
    type: 'evento',
    title: 'RadioFest 2026 — El festival de radios más grande de Latinoamérica',
    subtitle: '3 días · 30 radios · 50 artistas',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Massive%20outdoor%20music%20festival%20RadioFest%202026%20with%20multiple%20stages%20vibrant%20crowd%20colorful%20stage%20lighting%20spectacular%20evening%20atmosphere%20panoramic%20festival%20grounds%20professional%20event%20photography%20golden%20hour&width=800&height=450&seq=svd-thumb-evento-fest&orientation=landscape',
    authorName: 'RFW Oficial',
    authorPhoto: 'https://readdy.ai/api/search-image?query=Official%20RFW%20platform%20logo%20professional%20modern%20circular%20design%20white%20and%20gold%20accents%20confident%20branding%20emblem&width=120&height=120&seq=svd-author-rfw2&orientation=squarish',
    radioName: 'RFW Oficial',
    radioFreq: 'Global',
    tags: ['festival', 'música', 'networking'],
    savedAt: 'Hace 1 día',
    viewCount: 156000,
    isFavorite: true,
    isPending: true,
    scoreValue: 12,
    excerpt: 'El evento que reúne a toda la comunidad radial en un solo lugar. Guardalo para no perderte ningún detalle de la agenda, artistas y escenarios.',
  },
  {
    id: 4,
    type: 'concurso',
    title: 'La Voz de tu Ciudad — Ganá tu propio programa de radio en vivo',
    subtitle: 'Inscripciones abiertas hasta 30 de julio',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Professional%20microphone%20on%20stage%20under%20dramatic%20spotlight%20beam%20in%20dark%20concert%20venue%20atmosphere%20talent%20competition%20concept%20warm%20golden%20light%20against%20deep%20shadows%20professional%20photography%20cinematic%20mood&width=800&height=450&seq=svd-thumb-concurso-voz&orientation=landscape',
    authorName: 'Radio Juvenil FM',
    authorPhoto: 'https://readdy.ai/api/search-image?query=Radio%20station%20logo%20circular%20youthful%20energetic%20neon%20pink%20and%20cyan%20accents%20modern%20design%20professional%20broadcasting%20emblem&width=120&height=120&seq=svd-author-juvenil&orientation=squarish',
    radioName: 'Radio Juvenil FM',
    radioFreq: '105.3 FM',
    tags: ['concurso', 'talento', 'premio'],
    savedAt: 'Hace 2 días',
    viewCount: 34200,
    isFavorite: false,
    isPending: true,
    scoreValue: 10,
    excerpt: 'La búsqueda del mejor talento vocal de la ciudad. El ganador tendrá su propio espacio al aire. Revisá las bases y requisitos para participar.',
  },
  {
    id: 5,
    type: 'programa',
    title: 'El Despertador — Edición Especial 500 episodios',
    subtitle: 'Lun-Vie · 6:00-10:00 AM',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Morning%20radio%20show%20hosts%20celebrating%20milestone%20500th%20episode%20in%20professional%20broadcast%20studio%20with%20cake%20balloons%20and%20decorations%20joyful%20warm%20atmosphere%20golden%20morning%20light%20through%20studio%20window&width=800&height=450&seq=svd-thumb-programa-despertador&orientation=landscape',
    authorName: 'Radio Éxito FM',
    authorPhoto: 'https://readdy.ai/api/search-image?query=Radio%20station%20logo%20circular%20bright%20energetic%20warm%20orange%20and%20yellow%20tones%20professional%20broadcasting%20emblem%20contemporary%20design&width=120&height=120&seq=svd-author-exito&orientation=squarish',
    radioName: 'Radio Éxito FM',
    radioFreq: '98.5 FM',
    tags: ['matutino', 'especial', 'aniversario'],
    savedAt: 'Hace 3 días',
    viewCount: 67800,
    isFavorite: true,
    isPending: false,
    scoreValue: 6,
    excerpt: 'Medio millar de mañanas despertando a la ciudad. Guardá este programa para no perderte los bloques especiales con invitados sorpresa toda la semana.',
  },
  {
    id: 6,
    type: 'locutor',
    title: 'Ricky Navarro — La voz más escuchada de la mañana',
    subtitle: 'De pasante a estrella en solo 2 años',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Young%20charismatic%20male%20radio%20host%20in%20modern%20studio%20professional%20casual%20style%20warm%20genuine%20smile%20headphones%20around%20neck%20golden%20hour%20natural%20light%20through%20window%20premium%20portrait%20photography&width=800&height=450&seq=svd-thumb-locutor-ricky&orientation=landscape',
    authorName: 'Radio Élite Digital',
    authorPhoto: 'https://readdy.ai/api/search-image?query=Radio%20station%20logo%20modern%20circular%20minimalist%20design%20warm%20golden%20accents%20on%20dark%20background%20professional%20broadcasting%20emblem%20sleek%20contemporary&width=120&height=120&seq=svd-author-elite2&orientation=squarish',
    radioName: 'Radio Élite Digital',
    radioFreq: '99.1 FM',
    tags: ['locutor', 'inspiración', 'carrera'],
    savedAt: 'Hace 4 días',
    viewCount: 23100,
    isFavorite: true,
    isPending: false,
    scoreValue: 7,
    excerpt: 'La historia de cómo un joven apasionado por la radio se convirtió en el locutor estrella de la emisora digital más grande. Una inspiración total.',
  },
  {
    id: 7,
    type: 'radio',
    title: 'Estéreo Capital — La radio que revolucionó la escena local',
    subtitle: '102.1 FM · 850K oyentes mensuales',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Modern%20radio%20station%20building%20with%20illuminated%20signage%20at%20dusk%20warm%20glowing%20lights%20professional%20broadcast%20facility%20exterior%20architectural%20photography%20urban%20setting%20clean%20contemporary%20design&width=800&height=450&seq=svd-thumb-radio-estereo&orientation=landscape',
    authorName: 'Estéreo Capital',
    authorPhoto: 'https://readdy.ai/api/search-image?query=Radio%20station%20logo%20circular%20warm%20earthy%20tones%20professional%20broadcasting%20emblem%20contemporary%20minimalist%20design&width=120&height=120&seq=svd-author-capital&orientation=squarish',
    radioName: 'Estéreo Capital',
    radioFreq: '102.1 FM',
    tags: ['radio', 'top', 'comunidad'],
    savedAt: 'Hace 5 días',
    viewCount: 124500,
    isFavorite: true,
    isPending: false,
    scoreValue: 4,
    excerpt: 'La emisora que marcó un antes y después. Música, entretenimiento y la comunidad más grande de oyentes en la región. Tu radio de cabecera.',
  },
  {
    id: 8,
    type: 'post',
    title: 'Análisis: El futuro de la radio digital en América Latina — Tendencias 2026',
    subtitle: 'Datos, estadísticas y predicciones',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Futuristic%20radio%20broadcasting%20concept%20with%20holographic%20audio%20waveforms%20digital%20interface%20screens%20in%20modern%20studio%20environment%20warm%20ambient%20lighting%20technology%20meets%20tradition%20professional%20editorial%20photography&width=800&height=450&seq=svd-thumb-post-futuro&orientation=landscape',
    authorName: 'Radio Industria',
    authorPhoto: 'https://readdy.ai/api/search-image?query=Radio%20station%20logo%20circular%20industrial%20modern%20style%20metallic%20warm%20tones%20professional%20broadcasting%20emblem&width=120&height=120&seq=svd-author-industria&orientation=squarish',
    radioName: 'Radio Industria',
    radioFreq: '93.5 FM',
    tags: ['análisis', 'tendencias', 'futuro'],
    savedAt: 'Hace 6 días',
    viewCount: 56700,
    isFavorite: false,
    isPending: false,
    scoreValue: 5,
    excerpt: 'Un análisis profundo de hacia dónde va la industria. Crecimiento mobile, podcasts, monetización y el rol de las comunidades en la nueva era radial.',
  },
  {
    id: 9,
    type: 'video',
    title: 'Tutorial: Cómo hacer un jingle profesional para tu radio en 10 minutos',
    subtitle: 'Producción musical exprés',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Music%20producer%20working%20at%20digital%20audio%20workstation%20in%20creative%20home%20studio%20with%20MIDI%20keyboard%20monitors%20and%20acoustic%20panels%20warm%20ambient%20lighting%20professional%20creative%20workspace%20clean%20organized&width=800&height=450&seq=svd-thumb-video-jingle&orientation=landscape',
    authorName: 'Radio Peninsular',
    authorPhoto: 'https://readdy.ai/api/search-image?query=Radio%20station%20logo%20circular%20coastal%20inspired%20blue%20and%20warm%20sand%20tones%20professional%20broadcasting%20emblem%20contemporary%20design&width=120&height=120&seq=svd-author-peninsular&orientation=squarish',
    radioName: 'Radio Peninsular',
    radioFreq: '101.9 FM',
    tags: ['tutorial', 'producción', 'música'],
    savedAt: 'Hace 1 semana',
    viewCount: 41200,
    isFavorite: false,
    isPending: false,
    scoreValue: 8,
    excerpt: 'Paso a paso para crear un jingle con identidad propia. Sin experiencia previa en producción musical. Software gratuito y tips de experto.',
  },
  {
    id: 10,
    type: 'evento',
    title: 'Noche de Locutores — Homenaje a las voces que hacen historia',
    subtitle: '15 de agosto · Teatro Municipal',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Elegant%20theater%20venue%20prepared%20for%20radio%20awards%20ceremony%20with%20warm%20golden%20stage%20lighting%20red%20carpet%20entrance%20professional%20event%20setup%20sophisticated%20atmosphere%20empty%20seats%20before%20guests%20arrive%20anticipation&width=800&height=450&seq=svd-thumb-evento-noche&orientation=landscape',
    authorName: 'RFW Oficial',
    authorPhoto: 'https://readdy.ai/api/search-image?query=Official%20RFW%20platform%20logo%20professional%20modern%20circular%20design%20white%20and%20gold%20accents%20confident%20branding%20emblem&width=120&height=120&seq=svd-author-rfw3&orientation=squarish',
    radioName: 'RFW Oficial',
    radioFreq: 'Global',
    tags: ['gala', 'premiación', 'locutores'],
    savedAt: 'Hace 1 semana',
    viewCount: 28900,
    isFavorite: false,
    isPending: true,
    scoreValue: 12,
    excerpt: 'La noche más esperada del año radial. 12 categorías, alfombra roja, y todas las estrellas de la radio reunidas en una gala inolvidable.',
  },
  {
    id: 11,
    type: 'concurso',
    title: 'Crea tu Jingles 2026 — El concurso que ya suena al aire',
    subtitle: '$5,000 en premios · Jurado profesional',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Creative%20music%20competition%20concept%20with%20colorful%20sound%20waves%20vinyl%20records%20and%20award%20trophies%20on%20display%20professional%20studio%20backdrop%20warm%20spotlight%20illumination%20artistic%20composition%20editorial%20photography&width=800&height=450&seq=svd-thumb-concurso-jingles&orientation=landscape',
    authorName: 'Radio Peninsular',
    authorPhoto: 'https://readdy.ai/api/search-image?query=Radio%20station%20logo%20circular%20coastal%20inspired%20blue%20and%20warm%20sand%20tones%20professional%20broadcasting%20emblem%20contemporary%20design&width=120&height=120&seq=svd-author-peninsular2&orientation=squarish',
    radioName: 'Radio Peninsular',
    radioFreq: '101.9 FM',
    tags: ['concurso', 'creativo', 'premios'],
    savedAt: 'Hace 1 semana',
    viewCount: 19800,
    isFavorite: false,
    isPending: true,
    scoreValue: 10,
    excerpt: 'Más de 500 participantes ya enviaron sus creaciones. Los 3 ganadores verán sus jingles sonar al aire durante toda la temporada.',
  },
  {
    id: 12,
    type: 'programa',
    title: 'Deportes Total — Cobertura exclusiva del Mundial 2026',
    subtitle: 'Lun-Dom · 14:00-18:00',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Dynamic%20sports%20radio%20broadcast%20studio%20with%20multiple%20screens%20showing%20live%20matches%20hosts%20in%20passionate%20discussion%20sporty%20atmosphere%20professional%20broadcast%20equipment%20energetic%20lighting&width=800&height=450&seq=svd-thumb-programa-deportes&orientation=landscape',
    authorName: 'Radio Deportiva Total',
    authorPhoto: 'https://readdy.ai/api/search-image?query=Radio%20station%20logo%20circular%20sporty%20energetic%20red%20and%20dark%20tones%20professional%20broadcasting%20emblem%20contemporary%20dynamic%20design&width=120&height=120&seq=svd-author-deportiva&orientation=squarish',
    radioName: 'Radio Deportiva Total',
    radioFreq: '95.7 FM',
    tags: ['deportes', 'mundial', 'cobertura'],
    savedAt: 'Hace 1 semana',
    viewCount: 89300,
    isFavorite: true,
    isPending: false,
    scoreValue: 6,
    excerpt: 'El programa deportivo más completo. Cobertura minuto a minuto del Mundial con corresponsales en cada sede y análisis de expertos.',
  },
  {
    id: 13,
    type: 'locutor',
    title: 'Valentina Ríos — La reina de la tarde que conquistó a toda una generación',
    subtitle: '10 años al aire · 2 millones de oyentes',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Confident%20female%20radio%20host%20in%20elegant%20modern%20studio%20setting%20warm%20smile%20professional%20casual%20chic%20outfit%20natural%20lighting%20premium%20portrait%20photography%20sophisticated%20atmosphere&width=800&height=450&seq=svd-thumb-locutor-valentina&orientation=landscape',
    authorName: 'Estéreo Capital',
    authorPhoto: 'https://readdy.ai/api/search-image?query=Radio%20station%20logo%20circular%20warm%20earthy%20tones%20professional%20broadcasting%20emblem%20contemporary%20minimalist%20design&width=120&height=120&seq=svd-author-capital2&orientation=squarish',
    radioName: 'Estéreo Capital',
    radioFreq: '102.1 FM',
    tags: ['locutor', 'trayectoria', 'ícono'],
    savedAt: 'Hace 1 semana',
    viewCount: 45600,
    isFavorite: true,
    isPending: false,
    scoreValue: 7,
    excerpt: 'Una década siendo la compañía de las tardes. Su estilo único, su conexión con la audiencia y los secretos de su éxito revelados en este perfil.',
  },
  {
    id: 14,
    type: 'radio',
    title: 'Radio Éxito FM — La número 1 en ratings por 3 años consecutivos',
    subtitle: '98.5 FM · 1.2M oyentes mensuales',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Contemporary%20radio%20station%20broadcast%20tower%20at%20golden%20hour%20dramatic%20sky%20warm%20orange%20and%20pink%20tones%20professional%20architectural%20photography%20urban%20landscape%20impressive%20scale&width=800&height=450&seq=svd-thumb-radio-exito&orientation=landscape',
    authorName: 'Radio Éxito FM',
    authorPhoto: 'https://readdy.ai/api/search-image?query=Radio%20station%20logo%20circular%20bright%20energetic%20warm%20orange%20and%20yellow%20tones%20professional%20broadcasting%20emblem%20contemporary%20design&width=120&height=120&seq=svd-author-exito2&orientation=squarish',
    radioName: 'Radio Éxito FM',
    radioFreq: '98.5 FM',
    tags: ['radio', 'rating', 'top'],
    savedAt: 'Hace 2 semanas',
    viewCount: 198000,
    isFavorite: true,
    isPending: false,
    scoreValue: 4,
    excerpt: 'La emisora que domina las mañanas, las tardes y las noches. Una programación imbatible que la mantiene en la cima del rating nacional.',
  },
  {
    id: 15,
    type: 'post',
    title: 'Cómo los clubes de fans están transformando la lealtad de los oyentes',
    subtitle: 'Comunidad, exclusividad y engagement',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Diverse%20group%20of%20passionate%20radio%20fans%20gathering%20at%20meetup%20event%20warm%20community%20atmosphere%20wearing%20branded%20merchandise%20laughing%20and%20connecting%20candid%20photography%20golden%20hour%20outdoor%20setting&width=800&height=450&seq=svd-thumb-post-clubes&orientation=landscape',
    authorName: 'RFW Oficial',
    authorPhoto: 'https://readdy.ai/api/search-image?query=Official%20RFW%20platform%20logo%20professional%20modern%20circular%20design%20white%20and%20gold%20accents%20confident%20branding%20emblem&width=120&height=120&seq=svd-author-rfw4&orientation=squarish',
    radioName: 'RFW Oficial',
    radioFreq: 'Global',
    tags: ['clubes', 'comunidad', 'engagement'],
    savedAt: 'Hace 2 semanas',
    viewCount: 31200,
    isFavorite: false,
    isPending: false,
    scoreValue: 5,
    excerpt: 'Los datos no mienten: los oyentes que pertenecen a un club de fans consumen 3x más contenido y son 5x más propensos a recomendar la radio.',
  },
  {
    id: 16,
    type: 'video',
    title: 'Documental: 100 años de radio — Del AM al streaming global',
    subtitle: 'La historia completa en 45 minutos',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Vintage%20radio%20equipment%20museum%20display%20with%20warm%20nostalgic%20lighting%20antique%20microphones%20tube%20radios%20and%20broadcast%20memorabilia%20rich%20textures%20aged%20brass%20and%20wood%20tones%20documentary%20style%20photography&width=800&height=450&seq=svd-thumb-video-historia&orientation=landscape',
    authorName: 'RFW Oficial',
    authorPhoto: 'https://readdy.ai/api/search-image?query=Official%20RFW%20platform%20logo%20professional%20modern%20circular%20design%20white%20and%20gold%20accents%20confident%20branding%20emblem&width=120&height=120&seq=svd-author-rfw5&orientation=squarish',
    radioName: 'RFW Oficial',
    radioFreq: 'Global',
    tags: ['documental', 'historia', 'radio'],
    savedAt: 'Hace 2 semanas',
    viewCount: 67800,
    isFavorite: true,
    isPending: false,
    scoreValue: 8,
    excerpt: 'Un viaje fascinante por la evolución de la radio. Desde las primeras transmisiones AM hasta el streaming global que conecta millones hoy.',
  },
  {
    id: 17,
    type: 'evento',
    title: 'Taller: Locución Profesional — Técnicas de voz y presencia al aire',
    subtitle: '22 de julio · Online · Gratuito',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Professional%20voice%20coaching%20workshop%20in%20modern%20studio%20setting%20experienced%20instructor%20demonstrating%20microphone%20technique%20attentive%20students%20warm%20ambient%20lighting%20educational%20atmosphere%20focused%20engagement&width=800&height=450&seq=svd-thumb-evento-taller&orientation=landscape',
    authorName: 'Radio Industria',
    authorPhoto: 'https://readdy.ai/api/search-image?query=Radio%20station%20logo%20circular%20industrial%20modern%20style%20metallic%20warm%20tones%20professional%20broadcasting%20emblem&width=120&height=120&seq=svd-author-industria2&orientation=squarish',
    radioName: 'Radio Industria',
    radioFreq: '93.5 FM',
    tags: ['taller', 'formación', 'gratuito'],
    savedAt: 'Hace 2 semanas',
    viewCount: 15600,
    isFavorite: false,
    isPending: true,
    scoreValue: 12,
    excerpt: 'Aprendé técnicas profesionales de locución con los mejores del medio. Respiración, dicción, improvisación y presencia al micrófono.',
  },
  {
    id: 18,
    type: 'concurso',
    title: 'Copa RFW de Locución 2026 — La competencia definitiva de talento radial',
    subtitle: '4 categorías · Final en vivo',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Radio%20talent%20competition%20stage%20with%20finalists%20standing%20under%20dramatic%20spotlights%20golden%20trophy%20on%20display%20professional%20broadcast%20cameras%20capturing%20the%20moment%20intense%20atmosphere%20electric%20energy&width=800&height=450&seq=svd-thumb-concurso-copa&orientation=landscape',
    authorName: 'RFW Oficial',
    authorPhoto: 'https://readdy.ai/api/search-image?query=Official%20RFW%20platform%20logo%20professional%20modern%20circular%20design%20white%20and%20gold%20accents%20confident%20branding%20emblem&width=120&height=120&seq=svd-author-rfw6&orientation=squarish',
    radioName: 'RFW Oficial',
    radioFreq: 'Global',
    tags: ['competencia', 'locución', 'copa'],
    savedAt: 'Hace 2 semanas',
    viewCount: 42300,
    isFavorite: true,
    isPending: false,
    scoreValue: 10,
    excerpt: 'La competencia que enfrenta a los mejores locutores del continente. Noticias, deportes, entretenimiento y musical — 4 categorías, un solo campeón.',
  },
  {
    id: 19,
    type: 'programa',
    title: 'Noches de Jazz — El refugio sonoro de las medianoches',
    subtitle: 'Mar-Sáb · 22:00-01:00',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Intimate%20jazz%20radio%20studio%20at%20night%20warm%20amber%20lighting%20vintage%20microphone%20saxophone%20in%20background%20cozy%20sophisticated%20atmosphere%20vinyl%20records%20visible%20moody%20and%20elegant%20aesthetic&width=800&height=450&seq=svd-thumb-programa-jazz&orientation=landscape',
    authorName: 'Estéreo Capital',
    authorPhoto: 'https://readdy.ai/api/search-image?query=Radio%20station%20logo%20circular%20warm%20earthy%20tones%20professional%20broadcasting%20emblem%20contemporary%20minimalist%20design&width=120&height=120&seq=svd-author-capital3&orientation=squarish',
    radioName: 'Estéreo Capital',
    radioFreq: '102.1 FM',
    tags: ['jazz', 'nocturno', 'música'],
    savedAt: 'Hace 2 semanas',
    viewCount: 28900,
    isFavorite: true,
    isPending: false,
    scoreValue: 6,
    excerpt: 'El programa que te envuelve en el mejor jazz del mundo. Clásicos, contemporáneos y rarezas seleccionadas a mano por expertos en el género.',
  },
  {
    id: 20,
    type: 'locutor',
    title: 'Javier Mendoza — El hombre que le devolvió la pasión a la radio deportiva',
    subtitle: '25 años de trayectoria · 3 premios nacionales',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Experienced%20male%20sports%20radio%20commentator%20in%20dynamic%20broadcast%20booth%20passionate%20expression%20headset%20on%20surrounded%20by%20screens%20showing%20live%20sports%20warm%20professional%20lighting%20veteran%20presence&width=800&height=450&seq=svd-thumb-locutor-javier&orientation=landscape',
    authorName: 'Radio Deportiva Total',
    authorPhoto: 'https://readdy.ai/api/search-image?query=Radio%20station%20logo%20circular%20sporty%20energetic%20red%20and%20dark%20tones%20professional%20broadcasting%20emblem%20contemporary%20dynamic%20design&width=120&height=120&seq=svd-author-deportiva2&orientation=squarish',
    radioName: 'Radio Deportiva Total',
    radioFreq: '95.7 FM',
    tags: ['locutor', 'deportes', 'leyenda'],
    savedAt: 'Hace 3 semanas',
    viewCount: 34500,
    isFavorite: false,
    isPending: false,
    scoreValue: 7,
    excerpt: 'Un cuarto de siglo narrando los momentos más emocionantes del deporte. Su estilo único transformó la manera de contar el deporte en la radio.',
  },
  {
    id: 21,
    type: 'radio',
    title: 'Radio Juvenil FM — La emisora que habla el idioma de la nueva generación',
    subtitle: '105.3 FM · 620K oyentes mensuales',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Vibrant%20youthful%20radio%20station%20building%20with%20neon%20lighting%20and%20modern%20urban%20design%20creative%20energy%20street%20art%20mural%20exterior%20dusk%20atmosphere%20contemporary%20media%20hub%20photography&width=800&height=450&seq=svd-thumb-radio-juvenil&orientation=landscape',
    authorName: 'Radio Juvenil FM',
    authorPhoto: 'https://readdy.ai/api/search-image?query=Radio%20station%20logo%20circular%20youthful%20energetic%20neon%20pink%20and%20cyan%20accents%20modern%20design%20professional%20broadcasting%20emblem&width=120&height=120&seq=svd-author-juvenil2&orientation=squarish',
    radioName: 'Radio Juvenil FM',
    radioFreq: '105.3 FM',
    tags: ['radio', 'juvenil', 'tendencias'],
    savedAt: 'Hace 3 semanas',
    viewCount: 98700,
    isFavorite: false,
    isPending: false,
    scoreValue: 4,
    excerpt: 'La radio que entiende a los jóvenes. Música, cultura pop, gaming, redes sociales y todo lo que importa a la generación que está marcando el futuro.',
  },
  {
    id: 22,
    type: 'post',
    title: '10 estrategias para hacer crecer tu comunidad de radio en 30 días',
    subtitle: 'Plan de acción probado',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Strategic%20planning%20workspace%20with%20community%20growth%20charts%20analytics%20dashboard%20on%20screen%20sticky%20notes%20on%20whiteboard%20warm%20creative%20office%20environment%20organized%20and%20inspiring%20professional%20photography&width=800&height=450&seq=svd-thumb-post-estrategias&orientation=landscape',
    authorName: 'RFW Oficial',
    authorPhoto: 'https://readdy.ai/api/search-image?query=Official%20RFW%20platform%20logo%20professional%20modern%20circular%20design%20white%20and%20gold%20accents%20confident%20branding%20emblem&width=120&height=120&seq=svd-author-rfw7&orientation=squarish',
    radioName: 'RFW Oficial',
    radioFreq: 'Global',
    tags: ['crecimiento', 'estrategia', 'comunidad'],
    savedAt: 'Hace 3 semanas',
    viewCount: 72300,
    isFavorite: true,
    isPending: false,
    scoreValue: 5,
    excerpt: 'Un plan paso a paso que ya funcionó para más de 50 radios en la plataforma. Desde el engagement diario hasta los eventos comunitarios.',
  },
  {
    id: 23,
    type: 'video',
    title: 'Entrevista exclusiva: Carlos Vives habla de su relación con la radio',
    subtitle: '30 minutos de conversación íntima',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Famous%20Latin%20music%20artist%20being%20interviewed%20in%20intimate%20radio%20studio%20setting%20warm%20golden%20lighting%20genuine%20conversation%20professional%20broadcast%20cameras%20capturing%20the%20moment%20authentic%20connection&width=800&height=450&seq=svd-thumb-video-entrevista&orientation=landscape',
    authorName: 'Estéreo Capital',
    authorPhoto: 'https://readdy.ai/api/search-image?query=Radio%20station%20logo%20circular%20warm%20earthy%20tones%20professional%20broadcasting%20emblem%20contemporary%20minimalist%20design&width=120&height=120&seq=svd-author-capital4&orientation=squarish',
    radioName: 'Estéreo Capital',
    radioFreq: '102.1 FM',
    tags: ['entrevista', 'artista', 'música'],
    savedAt: 'Hace 3 semanas',
    viewCount: 134000,
    isFavorite: true,
    isPending: false,
    scoreValue: 8,
    excerpt: 'El ícono colombiano abre su corazón y cuenta cómo la radio fue su primera escuela, su primer amor y sigue siendo su medio favorito.',
  },
  {
    id: 24,
    type: 'evento',
    title: 'Encuentro de Radios Comunitarias — Conectando voces locales',
    subtitle: '5 de septiembre · Centro Cultural',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Community%20radio%20gathering%20in%20cultural%20center%20diverse%20group%20of%20broadcasters%20sharing%20ideas%20warm%20inclusive%20atmosphere%20round%20table%20discussion%20handmade%20radio%20banners%20natural%20daylight%20through%20windows%20documentary%20photography&width=800&height=450&seq=svd-thumb-evento-comunitario&orientation=landscape',
    authorName: 'RFW Oficial',
    authorPhoto: 'https://readdy.ai/api/search-image?query=Official%20RFW%20platform%20logo%20professional%20modern%20circular%20design%20white%20and%20gold%20accents%20confident%20branding%20emblem&width=120&height=120&seq=svd-author-rfw8&orientation=squarish',
    radioName: 'RFW Oficial',
    radioFreq: 'Global',
    tags: ['comunitario', 'networking', 'local'],
    savedAt: 'Hace 3 semanas',
    viewCount: 12300,
    isFavorite: false,
    isPending: true,
    scoreValue: 12,
    excerpt: 'Un espacio de encuentro para las radios que le dan voz a sus comunidades. Talleres, charlas y oportunidades de colaboración.',
  },
  {
    id: 25,
    type: 'concurso',
    title: 'Mejor Podcaster RFW 2026 — La nueva categoría que está rompiendo récords',
    subtitle: 'Votación del público + jurado internacional',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Podcast%20awards%20ceremony%20with%20nominees%20on%20stage%20dramatic%20gold%20and%20warm%20lighting%20professional%20event%20photography%20elegant%20atmosphere%20proud%20expressions%20anticipation%20and%20celebration&width=800&height=450&seq=svd-thumb-concurso-podcaster&orientation=landscape',
    authorName: 'RFW Oficial',
    authorPhoto: 'https://readdy.ai/api/search-image?query=Official%20RFW%20platform%20logo%20professional%20modern%20circular%20design%20white%20and%20gold%20accents%20confident%20branding%20emblem&width=120&height=120&seq=svd-author-rfw9&orientation=squarish',
    radioName: 'RFW Oficial',
    radioFreq: 'Global',
    tags: ['podcast', 'concurso', 'votación'],
    savedAt: 'Hace 3 semanas',
    viewCount: 56700,
    isFavorite: true,
    isPending: false,
    scoreValue: 10,
    excerpt: 'El podcasting explotó y RFW lo celebra con su propia competencia. Conocé a los nominados y votá por tu favorito antes del cierre.',
  },
  {
    id: 26,
    type: 'programa',
    title: 'Sábados de Rock — 4 horas de pura potencia sin pausa',
    subtitle: 'Sáb · 16:00-20:00',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Energetic%20rock%20radio%20show%20studio%20with%20hosts%20passionate%20about%20music%20electric%20guitar%20on%20wall%20vintage%20band%20posters%20vibrant%20lighting%20rock%20and%20roll%20atmosphere%20professional%20broadcast%20setup&width=800&height=450&seq=svd-thumb-programa-rock&orientation=landscape',
    authorName: 'Radio Peninsular',
    authorPhoto: 'https://readdy.ai/api/search-image?query=Radio%20station%20logo%20circular%20coastal%20inspired%20blue%20and%20warm%20sand%20tones%20professional%20broadcasting%20emblem%20contemporary%20design&width=120&height=120&seq=svd-author-peninsular3&orientation=squarish',
    radioName: 'Radio Peninsular',
    radioFreq: '101.9 FM',
    tags: ['rock', 'música', 'sábados'],
    savedAt: 'Hace 4 semanas',
    viewCount: 34500,
    isFavorite: false,
    isPending: false,
    scoreValue: 6,
    excerpt: 'El ritual de los sábados. Clásicos, rarezas, nuevos lanzamientos y la mejor selección de rock de todos los tiempos en un solo programa.',
  },
  {
    id: 27,
    type: 'locutor',
    title: 'Sofía Quintero — La voz serena que revolucionó las madrugadas',
    subtitle: 'Programa "Amanecer" · 4:00-6:00 AM',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Calm%20female%20radio%20host%20in%20quiet%20studio%20at%20dawn%20soft%20warm%20lighting%20through%20window%20gentle%20expression%20professional%20casual%20look%20peaceful%20atmosphere%20early%20morning%20broadcast%20setting&width=800&height=450&seq=svd-thumb-locutor-sofia&orientation=landscape',
    authorName: 'Radio Éxito FM',
    authorPhoto: 'https://readdy.ai/api/search-image?query=Radio%20station%20logo%20circular%20bright%20energetic%20warm%20orange%20and%20yellow%20tones%20professional%20broadcasting%20emblem%20contemporary%20design&width=120&height=120&seq=svd-author-exito3&orientation=squarish',
    radioName: 'Radio Éxito FM',
    radioFreq: '98.5 FM',
    tags: ['locutor', 'madrugada', 'serenidad'],
    savedAt: 'Hace 4 semanas',
    viewCount: 18900,
    isFavorite: false,
    isPending: false,
    scoreValue: 7,
    excerpt: 'Mientras la ciudad duerme, ella despierta a miles con su voz tranquila. La historia de cómo transformó la franja menos deseada en un éxito de audiencia.',
  },
  {
    id: 28,
    type: 'radio',
    title: 'Radio Deportiva Total — Cuando el deporte se vive con intensidad',
    subtitle: '95.7 FM · 720K oyentes mensuales',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Sports%20radio%20station%20interior%20with%20dynamic%20studio%20design%20multiple%20broadcast%20booths%20sports%20memorabilia%20on%20walls%20energetic%20atmosphere%20professional%20broadcast%20environment%20modern%20technology&width=800&height=450&seq=svd-thumb-radio-deportiva&orientation=landscape',
    authorName: 'Radio Deportiva Total',
    authorPhoto: 'https://readdy.ai/api/search-image?query=Radio%20station%20logo%20circular%20sporty%20energetic%20red%20and%20dark%20tones%20professional%20broadcasting%20emblem%20contemporary%20dynamic%20design&width=120&height=120&seq=svd-author-deportiva3&orientation=squarish',
    radioName: 'Radio Deportiva Total',
    radioFreq: '95.7 FM',
    tags: ['radio', 'deportes', 'pasión'],
    savedAt: 'Hace 4 semanas',
    viewCount: 145000,
    isFavorite: true,
    isPending: false,
    scoreValue: 4,
    excerpt: 'La casa del deporte en la radio. Transmisiones en vivo, debates apasionados, y la mejor cobertura de todas las disciplinas. Una radio que se vive.',
  },
  {
    id: 29,
    type: 'post',
    title: 'Guía de monetización: 7 formas de generar ingresos con tu radio digital',
    subtitle: 'De la publicidad al contenido premium',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Modern%20business%20professional%20reviewing%20revenue%20charts%20and%20analytics%20on%20tablet%20at%20clean%20desk%20with%20coffee%20warm%20natural%20lighting%20growth%20and%20success%20concept%20editorial%20photography&width=800&height=450&seq=svd-thumb-post-monetizacion&orientation=landscape',
    authorName: 'RFW Oficial',
    authorPhoto: 'https://readdy.ai/api/search-image?query=Official%20RFW%20platform%20logo%20professional%20modern%20circular%20design%20white%20and%20gold%20accents%20confident%20branding%20emblem&width=120&height=120&seq=svd-author-rfw10&orientation=squarish',
    radioName: 'RFW Oficial',
    radioFreq: 'Global',
    tags: ['monetización', 'ingresos', 'negocio'],
    savedAt: 'Hace 4 semanas',
    viewCount: 89200,
    isFavorite: true,
    isPending: false,
    scoreValue: 5,
    excerpt: 'Desde sponsors hasta membresías VIP. Un recorrido por todas las formas en que las radios de la plataforma están generando ingresos sostenibles.',
  },
  {
    id: 30,
    type: 'video',
    title: 'Así se hace un programa de radio en vivo — Detrás del vidrio',
    subtitle: 'Todo lo que el oyente no ve',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Inside%20view%20of%20professional%20radio%20studio%20during%20live%20broadcast%20multiple%20hosts%20at%20microphones%20producer%20behind%20glass%20window%20sound%20engineer%20at%20mixing%20console%20warm%20studio%20lighting%20dynamic%20working%20environment&width=800&height=450&seq=svd-thumb-video-detras&orientation=landscape',
    authorName: 'Radio Industria',
    authorPhoto: 'https://readdy.ai/api/search-image?query=Radio%20station%20logo%20circular%20industrial%20modern%20style%20metallic%20warm%20tones%20professional%20broadcasting%20emblem&width=120&height=120&seq=svd-author-industria3&orientation=squarish',
    radioName: 'Radio Industria',
    radioFreq: '93.5 FM',
    tags: ['detrás de escena', 'producción', 'técnica'],
    savedAt: 'Hace 1 mes',
    viewCount: 56700,
    isFavorite: false,
    isPending: false,
    scoreValue: 8,
    excerpt: 'Una mirada única al interior de una cabina durante un programa en vivo. La coordinación, los silencios, las señales y la magia de la radio.',
  },
  {
    id: 31,
    type: 'evento',
    title: 'Masterclass: Producción de Radio Digital — Del concepto a la transmisión',
    subtitle: '12 de agosto · Presencial y Online',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Professional%20workshop%20on%20radio%20production%20with%20instructor%20presenting%20to%20engaged%20audience%20modern%20conference%20room%20setting%20large%20screen%20with%20audio%20waveforms%20attendees%20taking%20notes%20warm%20educational%20atmosphere&width=800&height=450&seq=svd-thumb-evento-masterclass&orientation=landscape',
    authorName: 'RFW Oficial',
    authorPhoto: 'https://readdy.ai/api/search-image?query=Official%20RFW%20platform%20logo%20professional%20modern%20circular%20design%20white%20and%20gold%20accents%20confident%20branding%20emblem&width=120&height=120&seq=svd-author-rfw11&orientation=squarish',
    radioName: 'RFW Oficial',
    radioFreq: 'Global',
    tags: ['masterclass', 'producción', 'formación'],
    savedAt: 'Hace 1 mes',
    viewCount: 21000,
    isFavorite: false,
    isPending: true,
    scoreValue: 12,
    excerpt: '8 horas intensivas con los mejores productores de radio de la plataforma. Desde la idea hasta la transmisión en vivo con todos los detalles.',
  },
  {
    id: 32,
    type: 'concurso',
    title: 'Reto 30 Días de Contenido — Transformá tu radio con consistencia',
    subtitle: 'Premios semanales + Gran Premio Final',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Creative%20content%20calendar%20on%20wall%20with%20colorful%20sticky%20notes%20and%20planning%20materials%20inspiring%20workspace%20with%20warm%20lighting%20challenge%20concept%20motivation%20and%20organization%20professional%20photography&width=800&height=450&seq=svd-thumb-concurso-reto&orientation=landscape',
    authorName: 'RFW Oficial',
    authorPhoto: 'https://readdy.ai/api/search-image?query=Official%20RFW%20platform%20logo%20professional%20modern%20circular%20design%20white%20and%20gold%20accents%20confident%20branding%20emblem&width=120&height=120&seq=svd-author-rfw12&orientation=squarish',
    radioName: 'RFW Oficial',
    radioFreq: 'Global',
    tags: ['reto', 'contenido', 'crecimiento'],
    savedAt: 'Hace 1 mes',
    viewCount: 28900,
    isFavorite: false,
    isPending: false,
    scoreValue: 10,
    excerpt: 'El desafío que ya transformó más de 200 radios. 30 días publicando contenido diario con guías, tips y premios para los más consistentes.',
  },
  {
    id: 33,
    type: 'programa',
    title: 'La Hora del Cuento — Donde la imaginación se enciende',
    subtitle: 'Dom · 10:00-11:00 AM',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Cozy%20radio%20storytelling%20studio%20with%20warm%20lamp%20lighting%20comfortable%20armchair%20open%20storybook%20whimsical%20atmosphere%20children%20friendly%20setting%20soft%20colors%20magical%20gentle%20mood&width=800&height=450&seq=svd-thumb-programa-cuento&orientation=landscape',
    authorName: 'Radio Peninsular',
    authorPhoto: 'https://readdy.ai/api/search-image?query=Radio%20station%20logo%20circular%20coastal%20inspired%20blue%20and%20warm%20sand%20tones%20professional%20broadcasting%20emblem%20contemporary%20design&width=120&height=120&seq=svd-author-peninsular4&orientation=squarish',
    radioName: 'Radio Peninsular',
    radioFreq: '101.9 FM',
    tags: ['infantil', 'cuentos', 'familia'],
    savedAt: 'Hace 1 mes',
    viewCount: 41200,
    isFavorite: true,
    isPending: false,
    scoreValue: 6,
    excerpt: 'El programa que reúne a las familias alrededor de la radio. Cuentos, aventuras y personajes que despiertan la imaginación de grandes y chicos.',
  },
  {
    id: 34,
    type: 'locutor',
    title: 'Carlos Herrera — El hombre de las mil voces que transforma cada programa',
    subtitle: 'Actor de doblaje + locutor comercial',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Versatile%20male%20voice%20actor%20and%20radio%20host%20in%20professional%20recording%20booth%20with%20script%20expressive%20face%20multiple%20character%20voices%20warm%20studio%20lighting%20dynamic%20creative%20personality&width=800&height=450&seq=svd-thumb-locutor-carlos&orientation=landscape',
    authorName: 'Radio Industria',
    authorPhoto: 'https://readdy.ai/api/search-image?query=Radio%20station%20logo%20circular%20industrial%20modern%20style%20metallic%20warm%20tones%20professional%20broadcasting%20emblem&width=120&height=120&seq=svd-author-industria4&orientation=squarish',
    radioName: 'Radio Industria',
    radioFreq: '93.5 FM',
    tags: ['locutor', 'versátil', 'voces'],
    savedAt: 'Hace 1 mes',
    viewCount: 22300,
    isFavorite: false,
    isPending: false,
    scoreValue: 7,
    excerpt: 'De hacer voces para películas a ser la voz más reconocida de la radio local. Su capacidad camaleónica lo hace único en cada programa.',
  },
  {
    id: 35,
    type: 'radio',
    title: 'Radio Peninsular — Donde la brisa del mar se escucha',
    subtitle: '101.9 FM · 480K oyentes mensuales',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Coastal%20radio%20station%20building%20with%20ocean%20view%20warm%20sunset%20colors%20palm%20trees%20in%20background%20relaxed%20tropical%20atmosphere%20professional%20broadcast%20facility%20serene%20and%20inviting%20architectural%20photography&width=800&height=450&seq=svd-thumb-radio-peninsular&orientation=landscape',
    authorName: 'Radio Peninsular',
    authorPhoto: 'https://readdy.ai/api/search-image?query=Radio%20station%20logo%20circular%20coastal%20inspired%20blue%20and%20warm%20sand%20tones%20professional%20broadcasting%20emblem%20contemporary%20design&width=120&height=120&seq=svd-author-peninsular5&orientation=squarish',
    radioName: 'Radio Peninsular',
    radioFreq: '101.9 FM',
    tags: ['radio', 'costera', 'tropical'],
    savedAt: 'Hace 1 mes',
    viewCount: 78900,
    isFavorite: true,
    isPending: false,
    scoreValue: 4,
    excerpt: 'La radio que suena a playa, a brisa y a buena vibra. Una programación que mezcla lo mejor de la música tropical con la cultura costera.',
  },
];

export const savedUserProfile: SavedUserProfile = {
  id: 1,
  name: 'Carlos Mendoza',
  photo: 'https://readdy.ai/api/search-image?query=Friendly%20latino%20man%20mid%20twenties%20clean%20casual%20style%20warm%20smile%20premium%20portrait%20photography%20soft%20warm%20neutral%20background%20subtle%20golden%20glow%20editorial%20quality&width=120&height=120&seq=svd-user-carlos&orientation=squarish',
  totalSaved: 35,
  totalViews: 12400,
  totalFavorites: 18,
  totalPending: 7,
  streak: 12,
};

export const savedDashboardStats: SavedDashboardStats = {
  totalSaved: 125400,
  totalSavedToday: 3400,
  mostSavedType: 'Radios',
  mostSavedTypeCount: 31200,
  totalViews: 2840000,
  totalFavorites: 45200,
  totalPending: 12300,
  totalScoreGenerated: 875000,
  avgSavedPerUser: 7.8,
};

export const saveScoreActions: SaveScoreAction[] = [
  { id: 'guardar-contenido', label: 'Guardar Contenido', description: 'Cada vez que guardás un post, video, evento, concurso, programa, locutor o radio', points: 3, icon: 'ri-bookmark-line', color: 'text-teal-600' },
  { id: 'contenido-guardado-visto', label: 'Contenido Guardado Visto', description: 'Volvés a ver algo que guardaste previamente', points: 5, icon: 'ri-eye-line', color: 'text-sky-600' },
  { id: 'guardado-compartido', label: 'Guardado Compartido', description: 'Compartís algo que tenías guardado con la comunidad', points: 8, icon: 'ri-share-forward-line', color: 'text-amber-600' },
  { id: 'racha-guardados', label: 'Racha de Guardados', description: 'Guardás contenido 7 días seguidos', points: 50, icon: 'ri-flashlight-line', color: 'text-emerald-600' },
  { id: 'coleccion-completa', label: 'Colección Completa', description: 'Alcanzás 50 items guardados en tu perfil', points: 100, icon: 'ri-stack-line', color: 'text-violet-600' },
  { id: 'favorito-top', label: 'Favorito Top', description: 'Uno de tus guardados llega al top 10 de contenido más guardado', points: 25, icon: 'ri-star-line', color: 'text-orange-600' },
  { id: 'guardado-pendiente-visto', label: 'Pendiente Completado', description: 'Marcás como visto un contenido que tenías pendiente', points: 4, icon: 'ri-check-double-line', color: 'text-rose-600' },
];

export function getSavedItemsByFilter(filter: SavedFilterId): SavedItem[] {
  switch (filter) {
    case 'recientes':
      return [...savedItems].sort((a, b) => a.id - b.id);
    case 'mas-vistos':
      return [...savedItems].sort((a, b) => b.viewCount - a.viewCount);
    case 'favoritos':
      return savedItems.filter((item) => item.isFavorite);
    case 'pendientes':
      return savedItems.filter((item) => item.isPending);
    default:
      return savedItems;
  }
}

export function getSavedItemsByType(typeId: SavedContentTypeId): SavedItem[] {
  return savedItems.filter((item) => item.type === typeId);
}

export function getContentTypeById(id: SavedContentTypeId): SavedContentType | undefined {
  return savedContentTypes.find((ct) => ct.id === id);
}

export function getTopSavedItems(limit = 6): SavedItem[] {
  return [...savedItems].sort((a, b) => b.viewCount - a.viewCount).slice(0, limit);
}

export function getFavorites(limit = 6): SavedItem[] {
  return savedItems.filter((item) => item.isFavorite).slice(0, limit);
}

export function getPendingItems(limit = 6): SavedItem[] {
  return savedItems.filter((item) => item.isPending).slice(0, limit);
}